import React, { useState, useEffect, useCallback } from 'react';
import { leaveAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Check, X, CalendarOff, AlertTriangle, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  declined: 'bg-red-500/15 text-red-400 border-red-500/20',
  cancelled: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
};

const TYPE_LABELS = {
  annual: 'Annual Leave',
  sick: 'Sick Leave',
  unpaid: 'Unpaid Leave',
  other: 'Other',
};

const TYPE_STYLES = {
  annual: 'bg-[#1ABC9C]/10 text-[#1ABC9C]',
  sick: 'bg-red-500/10 text-red-400',
  unpaid: 'bg-indigo-500/10 text-indigo-400',
  other: 'bg-white/10 text-[#A1A1AA]',
};

const fmtDate = (d) => format(parseISO(d), 'EEE d MMM yyyy');

export default function Leave() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [notes, setNotes] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await leaveAPI.list(params);
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async () => {
    if (!approveTarget) return;
    setProcessingId(approveTarget.id);
    try {
      const res = await leaveAPI.approve(approveTarget.id, notes || undefined);
      const unassigned = res.data.unassigned_shifts || 0;
      toast.success(
        unassigned > 0
          ? `Leave approved — ${unassigned} clashing shift${unassigned !== 1 ? 's' : ''} unassigned`
          : 'Leave approved'
      );
      setApproveTarget(null);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async () => {
    if (!declineTarget) return;
    setProcessingId(declineTarget.id);
    try {
      await leaveAPI.decline(declineTarget.id, notes || undefined);
      toast.success('Leave request declined');
      setDeclineTarget(null);
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline leave');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
            <CalendarOff className="w-6 h-6 text-[#1ABC9C]" />
            Leave Requests
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Review and manage staff leave</p>
        </div>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="bg-[#2B2B2B] border border-white/5">
          <TabsTrigger value="pending" data-testid="leave-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="leave-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="declined" data-testid="leave-tab-declined">Declined</TabsTrigger>
          <TabsTrigger value="all" data-testid="leave-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="bg-[#2B2B2B] border-white/5">
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="p-12 text-center text-[#A1A1AA]" data-testid="leave-empty-state">
              <CalendarOff className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No {statusFilter !== 'all' ? statusFilter : ''} leave requests
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Staff</TableHead>
                    <TableHead className="text-[#A1A1AA]">Type</TableHead>
                    <TableHead className="text-[#A1A1AA]">Dates</TableHead>
                    <TableHead className="text-[#A1A1AA]">Days</TableHead>
                    <TableHead className="text-[#A1A1AA]">Reason</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status / Conflicts</TableHead>
                    <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((req) => (
                    <TableRow key={req.id} className="border-white/5" data-testid={`leave-row-${req.id}`}>
                      <TableCell className="text-[#F2F2F2] font-medium">{req.medic_name}</TableCell>
                      <TableCell>
                        <Badge className={`${TYPE_STYLES[req.leave_type]} border-0`}>
                          {TYPE_LABELS[req.leave_type] || req.leave_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#A1A1AA] text-sm whitespace-nowrap">
                        {fmtDate(req.start_date)} → {fmtDate(req.end_date)}
                      </TableCell>
                      <TableCell className="text-[#F2F2F2] font-['JetBrains_Mono']">{req.days}</TableCell>
                      <TableCell className="text-[#A1A1AA] text-sm max-w-[200px] truncate">{req.reason || '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={`${STATUS_STYLES[req.status]} border`}>
                            {req.status}
                          </Badge>
                          {req.status === 'pending' && req.conflict_count > 0 && (
                            <Badge
                              className="bg-red-500/15 text-red-400 border-red-500/20 border flex items-center gap-1"
                              data-testid={`leave-conflicts-badge-${req.id}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {req.conflict_count} shift clash{req.conflict_count !== 1 ? 'es' : ''}
                            </Badge>
                          )}
                        </div>
                        {req.reviewer_notes && (
                          <p className="text-xs text-[#A1A1AA] mt-1">Note: {req.reviewer_notes}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {req.status === 'pending' ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              className="bg-[#1ABC9C] hover:bg-[#16a085] text-white"
                              disabled={processingId === req.id}
                              onClick={() => { setNotes(''); setApproveTarget(req); }}
                              data-testid={`approve-leave-btn-${req.id}`}
                            >
                              {processingId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              disabled={processingId === req.id}
                              onClick={() => { setNotes(''); setDeclineTarget(req); }}
                              data-testid={`decline-leave-btn-${req.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-[#A1A1AA] flex items-center justify-end gap-1">
                            <Clock className="w-3 h-3" />
                            {req.reviewed_by || '—'}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(open) => !open && setApproveTarget(null)}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Approve Leave Request</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {approveTarget && (
                <>
                  {approveTarget.medic_name} — {TYPE_LABELS[approveTarget.leave_type]}, {fmtDate(approveTarget.start_date)} to {fmtDate(approveTarget.end_date)} ({approveTarget.days} day{approveTarget.days !== 1 ? 's' : ''})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {approveTarget?.conflicts?.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-2" data-testid="approve-conflict-warning">
              <p className="text-sm text-red-400 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {approveTarget.conflicts.length} assigned shift{approveTarget.conflicts.length !== 1 ? 's' : ''} will be automatically unassigned:
              </p>
              <ul className="text-xs text-[#A1A1AA] space-y-1 max-h-32 overflow-y-auto">
                {approveTarget.conflicts.map((c) => (
                  <li key={c.shift_id}>• {c.date} {c.start_time}–{c.end_time} @ {c.location}</li>
                ))}
              </ul>
            </div>
          )}
          <Textarea
            placeholder="Optional note for the medic..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-[#1A1A1A] border-white/10"
            data-testid="approve-leave-notes"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveTarget(null)}>Cancel</Button>
            <Button
              className="bg-[#1ABC9C] hover:bg-[#16a085] text-white"
              onClick={handleApprove}
              disabled={!!processingId}
              data-testid="confirm-approve-leave-btn"
            >
              {processingId ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Approve Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={!!declineTarget} onOpenChange={(open) => !open && setDeclineTarget(null)}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Decline Leave Request</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {declineTarget && (
                <>
                  {declineTarget.medic_name} — {TYPE_LABELS[declineTarget.leave_type]}, {fmtDate(declineTarget.start_date)} to {fmtDate(declineTarget.end_date)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for declining (visible to the medic)..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="bg-[#1A1A1A] border-white/10"
            data-testid="decline-leave-notes"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeclineTarget(null)}>Cancel</Button>
            <Button
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={handleDecline}
              disabled={!!processingId}
              data-testid="confirm-decline-leave-btn"
            >
              {processingId ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
